import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, IconButton, LinearProgress, Switch, TextField, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JobService from '../../services/JobService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { SectionCard } from '../common/ui';
import ConfirmDialog from '../common/ConfirmDialog';
import { STEP_KIND, activeSteps, jobPayload } from '../../utils/workflows';
import { fmtDate, todayIso } from '../../utils/format';
import { ACCEPT_IMAGE, ACCEPT_PDF, PdfRow, PhotoThumb, useFilePicker } from './files';

/** Hidden picker + button for uploading into one step. */
function StepUpload({ accept, capture, icon, label, onFiles, disabled }) {
    const [input, open] = useFilePicker(onFiles, { accept, multiple: !accept.includes('pdf'), capture });
    return (
        <>
            {input}
            <Button size="small" variant="outlined" startIcon={icon} onClick={open} disabled={disabled}
                    sx={{ fontSize: 12.5, py: 0.25, whiteSpace: 'nowrap' }}>{label}</Button>
        </>
    );
}

/**
 * The category's checklist: date steps take a picked date, ticks are stamped with today,
 * file steps complete with their upload and the visit step with the first logged visit.
 */
/** readOnly (employees): the checklist is shown but nothing can be ticked, uploaded to a step or deleted. */
export default function WorkflowCard({ job, files, visits, onJobChange, onMarkDone, readOnly, upload, uploading, openFile, askDelete, onShowVisits }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(null);
    const steps = activeSteps(job);
    const done = steps.filter(s => job.steps[s]).length;
    const allDone = steps.length > 0 && done === steps.length;

    // Optimistic: the tick shows at once (slow mobile data in the field) and rolls back on error. Resolves true on success.
    const setStep = async (code, isDone, date) => {
        setBusy(code);
        const nextSteps = { ...job.steps };
        if (isDone) nextSteps[code] = date || todayIso(); else delete nextSteps[code];
        onJobChange({ ...job, steps: nextSteps });
        try {
            onJobChange(await JobService.setStep(job.id, code, isDone, date));
            return true;
        } catch (err) {
            onJobChange(job);
            showToast(errorMessage(err, t('common.error')), 'error');
            return false;
        } finally {
            setBusy(null);
        }
    };

    // A done step is not undone or re-dated by one stray click: dates are edited behind a pencil and saved with ✓,
    // undoing asks first, and both offer Undo (restoring the previous date) in the toast.
    const [editing, setEditing] = useState(null); // { code, draft }
    const [confirmUndo, setConfirmUndo] = useState(null); // step code
    const undoStep = async (code) => {
        const prev = job.steps[code];
        setConfirmUndo(null);
        setEditing(null);
        if (await setStep(code, false)) {
            showToast(t('workflow.removed', { step: t(`steps.${code}`) }), 'info',
                { action: { label: t('workflow.undo'), onClick: () => setStep(code, true, prev) } });
        }
    };
    const saveDate = async () => {
        const { code, draft } = editing;
        const prev = job.steps[code];
        setEditing(null);
        if (!draft || draft < '1900' || draft === prev) return;
        if (await setStep(code, true, draft)) {
            showToast(t('workflow.dateChanged', { date: fmtDate(draft) }), 'success',
                { action: { label: t('workflow.undo'), onClick: () => setStep(code, true, prev) } });
        }
    };

    const toggleParcelling = async (include) => {
        setBusy('PERMIT_PARCELLING');
        onJobChange({ ...job, includeParcelling: include });
        try {
            onJobChange(await JobService.update(job.id, jobPayload(job, { includeParcelling: include })));
        } catch (err) {
            onJobChange(job);
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setBusy(null);
        }
    };


    // Waiting for the cadastre: submitted, and something the cadastre sends back is still missing.
    const submitted = job.steps.SUBMITTED;
    const awaiting = submitted && job.status !== 'DONE'
        && ['CERTIFICATE', 'DECISION'].some(s => steps.includes(s) && !job.steps[s]);
    const waitingDays = awaiting ? Math.max(0, Math.round((new Date(todayIso()) - new Date(submitted)) / 86400000)) : 0;

    const stepFiles = (code) => files.filter(f => f.stepCode === code);

    const renderAction = (code) => {
        const kind = STEP_KIND[code];
        const value = job.steps[code];
        const disabled = busy === code;
        if (readOnly) return null;
        if (kind === 'DATE') {
            const dateInput = (v, onChange) => (
                <TextField type="date" size="small" value={v || ''} disabled={disabled} onChange={onChange}
                           inputProps={{ 'aria-label': t(`steps.${code}`) }}
                           sx={{ width: 158, '& input': { py: 0.75, fontSize: 13 } }} />
            );
            // First date: saved straight away. Date inputs fire while the year is typed digit by digit; only save real years.
            if (!value) return dateInput('', (e) => e.target.value >= '1900' && setStep(code, true, e.target.value));
            if (editing?.code === code) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
                        {dateInput(editing.draft, (e) => setEditing({ code, draft: e.target.value }))}
                        <Tooltip title={t('workflow.saveDate')}><IconButton size="small" color="success" onClick={saveDate}><CheckIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        <Tooltip title={t('workflow.cancelEdit')}><IconButton size="small" onClick={() => setEditing(null)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        <Button size="small" color="error" onClick={() => setConfirmUndo(code)} sx={{ fontSize: 12, minWidth: 0 }}>{t('workflow.removeDate')}</Button>
                    </Box>
                );
            }
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#059669', whiteSpace: 'nowrap' }}>{fmtDate(value)}</Typography>
                    <Tooltip title={t('workflow.editDate')}>
                        <IconButton size="small" disabled={disabled} onClick={() => setEditing({ code, draft: value })} aria-label={t('workflow.editDate')}>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        }
        if (kind === 'PDF' || kind === 'CHECK_PDF') {
            const has = stepFiles(code).length > 0;
            return (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {code === 'CLIENT_RECEIPT' && (
                        <Button size="small" startIcon={<PrintOutlinedIcon />} onClick={() => navigate(`/jobs/${job.id}/print/receipt`)}
                                sx={{ fontSize: 12.5, py: 0.25, whiteSpace: 'nowrap' }}>{t('workflow.printForm')}</Button>
                    )}
                    <StepUpload accept={ACCEPT_PDF} icon={<UploadFileOutlinedIcon />} disabled={uploading}
                                label={has ? t('workflow.replacePdf') : t('workflow.uploadPdf')}
                                onFiles={(fl) => upload(fl, { stepCode: code })} />
                </Box>
            );
        }
        if (kind === 'IMAGE') {
            return <StepUpload accept={ACCEPT_IMAGE} icon={<AddAPhotoOutlinedIcon />} label={t('workflow.addPhoto')} disabled={uploading}
                               onFiles={(fl) => upload(fl, { stepCode: code })} />;
        }
        if (kind === 'VISITS') {
            return <Button size="small" onClick={onShowVisits} sx={{ fontSize: 12.5 }}>{t('visits.add')}</Button>;
        }
        return null;
    };

    const renderSub = (code) => {
        const kind = STEP_KIND[code];
        const value = job.steps[code];
        if (kind === 'VISITS') {
            return visits.length
                ? t('workflow.visitsSummary', { count: visits.length, date: fmtDate(visits[0].visitDate) })
                : t('workflow.visitsNone');
        }
        if (kind === 'DATE') return null;
        return value ? t('workflow.doneOn', { date: fmtDate(value) }) : null;
    };

    return (
        <SectionCard title={t('workflow.title')}
                     action={<Typography sx={{ fontSize: 12.5, fontWeight: 700, color: allDone ? '#059669' : '#64748B' }}>{t('workflow.progress', { done, total: steps.length })}</Typography>}
                     sx={{ mb: 2.5 }}>
            <LinearProgress variant="determinate" value={steps.length ? (done / steps.length) * 100 : 0}
                            sx={{ height: 3, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { bgcolor: allDone ? '#10B981' : '#427898' } }} />
            {uploading && <LinearProgress />}

            {awaiting && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 2.5, mt: 2, px: 1.5, py: 1, borderRadius: '10px', bgcolor: 'rgba(245,158,11,0.10)', color: '#B45309' }}>
                    <HourglassTopIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t('workflow.waitingDays', { count: waitingDays })}</Typography>
                </Box>
            )}

            {job.category.workflow === 'BUILDING_PERMIT' && (
                <Box sx={{ px: 2.5, pt: 1.5 }}>
                    <FormControlLabel
                        control={<Switch size="small" checked={job.includeParcelling} disabled={readOnly || busy === 'PERMIT_PARCELLING'} onChange={(e) => toggleParcelling(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 13.5 }}>{t('workflow.includeParcelling')}</Typography>} />
                </Box>
            )}

            <Box sx={{ px: 2.5, py: 1 }}>
                {steps.map(code => {
                    const kind = STEP_KIND[code];
                    const isDone = !!job.steps[code];
                    const checkable = kind === 'CHECK' || kind === 'CHECK_PDF';
                    const sub = renderSub(code);
                    const own = stepFiles(code);
                    const photos = own.filter(f => f.kind === 'IMAGE');
                    return (
                        <Box key={code} sx={{ py: 1.25, borderBottom: '1px solid #F5F7FA', '&:last-of-type': { borderBottom: 0 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                {checkable && !readOnly ? (
                                    <Checkbox checked={isDone} disabled={busy === code} onChange={(e) => (e.target.checked ? setStep(code, true) : setConfirmUndo(code))}
                                              icon={<RadioButtonUncheckedIcon />} checkedIcon={<CheckCircleIcon />}
                                              sx={{ p: 0.25, color: '#CBD5E1', '&.Mui-checked': { color: '#10B981' } }}
                                              inputProps={{ 'aria-label': t(`steps.${code}`) }} />
                                ) : isDone
                                    ? <CheckCircleIcon sx={{ color: '#10B981', m: 0.25 }} />
                                    : <RadioButtonUncheckedIcon sx={{ color: '#CBD5E1', m: 0.25 }} />}
                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: isDone ? '#1a2332' : '#475569' }}>{t(`steps.${code}`)}</Typography>
                                    {sub && <Typography sx={{ fontSize: 12, color: isDone ? '#059669' : '#94A3B8' }}>{sub}</Typography>}
                                </Box>
                                <Box sx={{ ml: { xs: 4.5, sm: 0 } }}>{renderAction(code)}</Box>
                            </Box>
                            {own.filter(f => f.kind === 'PDF').map(f => (
                                <Box key={f.id} sx={{ ml: 4.5 }}>
                                    <PdfRow dense owner={job.id} file={f} onOpen={() => openFile([f], 0)} onDelete={askDelete ? () => askDelete(f) : undefined} />
                                </Box>
                            ))}
                            {photos.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 4.5, mt: 1 }}>
                                    {photos.map((f, i) => <PhotoThumb key={f.id} owner={job.id} file={f} size={72} onOpen={() => openFile(photos, i)} />)}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {allDone && job.status !== 'DONE' && !readOnly && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mx: 2.5, mb: 2, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: 'rgba(16,185,129,0.08)' }}>
                    <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#047857', minWidth: 200 }}>{t('workflow.allDone')}</Typography>
                    <Button size="small" variant="contained" color="success" disableElevation onClick={onMarkDone}>{t('workflow.markDone')}</Button>
                </Box>
            )}
            <ConfirmDialog open={!!confirmUndo} title={t('workflow.confirmRemoveTitle')} confirmLabel={t('workflow.confirmRemove')}
                           text={confirmUndo ? t('workflow.confirmRemoveText', { step: t(`steps.${confirmUndo}`), date: fmtDate(job.steps[confirmUndo]) }) : ''}
                           onConfirm={() => undoStep(confirmUndo)} onClose={() => setConfirmUndo(null)} />
        </SectionCard>
    );
}
